import React, { useState, useEffect } from 'react';
import { View, Text, Pressable, StyleSheet, Platform, ActivityIndicator, Modal, TextInput } from 'react-native';
import WebScrollView from '../components/WebScrollView';
import { Users, Plus, X, Loader2, Calendar, ArrowLeft, ChevronDown } from '../lib/icons';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { shadows } from '../lib/styles';
import { useActiveHome } from '../contexts/ActiveHomeContext';
import HomePicker from '../components/HomePicker';

interface Service {
  id: string;
  name: string;
  provider: string;
  frequency: string;
  last_service_date: string;
  next_service_date: string;
  notes: string;
  status: 'active' | 'paused' | 'cancelled';
}

const serviceTypes = [
  { id: 'landscaping', label: 'Landscaping' },
  { id: 'housekeeping', label: 'Housekeeping' },
  { id: 'pest_control', label: 'Pest Control' },
  { id: 'pool_maintenance', label: 'Pool Maintenance' },
  { id: 'hvac', label: 'HVAC Service' },
  { id: 'plumbing', label: 'Plumbing' },
  { id: 'electrical', label: 'Electrical' },
  { id: 'roofing', label: 'Roofing' },
  { id: 'cleaning', label: 'Deep Cleaning' }
];

const frequencies = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semiannual', label: 'Semi-annual' },
  { value: 'annual', label: 'Annual' },
  { value: 'asneeded', label: 'As Needed' }
];

const ManagedServices = () => {
  const { homes, activeHome, setActiveHomeById } = useActiveHome();
  const navigate = useNavigate();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showServiceTypePicker, setShowServiceTypePicker] = useState(false);
  const [showFrequencyPicker, setShowFrequencyPicker] = useState(false);
  const [formData, setFormData] = useState({
    name: serviceTypes[0].id,
    provider: '',
    frequency: frequencies[0].value,
    last_service_date: '',
    notes: '',
    status: 'active' as const
  });

  useEffect(() => {
    if (homes.length === 0) {
      setServices([]);
      setLoading(false);
      return;
    }
    fetchServices();
  }, [homes.length, activeHome?.id]);

  const fetchServices = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setLoading(false);
        return;
      }

      const userHomes = homes;

      if (!userHomes || userHomes.length === 0) {
        setServices([]);
        setLoading(false);
        return;
      }

      // Fetch services only for user's homes
      const { data, error } = await supabase
        .from('managed_services')
        .select('*')
        .in('home_id', userHomes.map(h => h.id))
        .order('name', { ascending: true });

      if (error) throw error;
      setServices(data || []);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');

    // Get current user and their homes
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      setError('You must be logged in to add a service');
      setSubmitting(false);
      return;
    }

    const userHomes = homes;

    if (!userHomes || userHomes.length === 0) {
      setError('You must have at least one home to add a service');
      setSubmitting(false);
      return;
    }

    const homeId = activeHome?.id || userHomes[0].id;

    const lastServiceDate = new Date(formData.last_service_date);
    let nextServiceDate = new Date(lastServiceDate);
    
    switch (formData.frequency) {
      case 'weekly':
        nextServiceDate.setDate(nextServiceDate.getDate() + 7);
        break;
      case 'biweekly':
        nextServiceDate.setDate(nextServiceDate.getDate() + 14);
        break;
      case 'monthly':
        nextServiceDate.setMonth(nextServiceDate.getMonth() + 1);
        break;
      case 'quarterly':
        nextServiceDate.setMonth(nextServiceDate.getMonth() + 3);
        break;
      case 'semiannual':
        nextServiceDate.setMonth(nextServiceDate.getMonth() + 6);
        break;
      case 'annual':
        nextServiceDate.setFullYear(nextServiceDate.getFullYear() + 1);
        break;
      case 'asneeded':
        nextServiceDate = lastServiceDate;
        break;
    }

    try {
      const { error } = await supabase
        .from('managed_services')
        .insert([{
          ...formData,
          home_id: homeId,
          next_service_date: nextServiceDate.toISOString().split('T')[0]
        }]);

      if (error) throw error;

      setFormData({
        name: serviceTypes[0].id,
        provider: '',
        frequency: frequencies[0].value,
        last_service_date: '',
        notes: '',
        status: 'active'
      });
      setIsFormOpen(false);
      setActiveHomeById(homeId);
      fetchServices();
    } catch (error: any) {
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: Service['status']) => {
    switch (status) {
      case 'active':
        return { text: '#16a34a', bg: '#f0fdf4' };
      case 'paused':
        return { text: '#ea580c', bg: '#fff7ed' };
      case 'cancelled':
        return { text: '#dc2626', bg: '#fef2f2' };
      default:
        return { text: '#4b5563', bg: '#f9fafb' };
    }
  };

  const getServiceLabel = (serviceId: string) => {
    return serviceTypes.find(type => type.id === serviceId)?.label || serviceId;
  };

  const getFrequencyLabel = (frequencyValue: string) => {
    return frequencies.find(f => f.value === frequencyValue)?.label || frequencyValue;
  };

  return (
    <WebScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <View style={styles.header}>
        <View style={styles.headerTop}>
          <Pressable
            onPress={() => navigate(-1)}
            style={styles.backButton}
          >
            <ArrowLeft size={20} color="#9ca3af" />
          </Pressable>
          <View style={styles.headerText}>
            <Text style={styles.title}>Managed Services</Text>
            <Text style={styles.subtitle}>Track and manage your recurring services</Text>
          </View>
        </View>
        <Pressable
          onPress={() => setIsFormOpen(true)}
          style={styles.addButton}
        >
          <Plus size={16} color="#ffffff" />
          <Text style={styles.addButtonText}>Add Service</Text>
        </Pressable>
      </View>

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#9ca3af" />
        </View>
      ) : services.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Users size={48} color="#9ca3af" />
          <Text style={styles.emptyTitle}>No Services Added</Text>
          <Text style={styles.emptyText}>Start tracking your managed services</Text>
          <Pressable
            onPress={() => setIsFormOpen(true)}
            style={styles.emptyAddButton}
          >
            <Plus size={16} color="#ffffff" />
            <Text style={styles.emptyAddButtonText}>Add Your First Service</Text>
          </Pressable>
        </View>
      ) : (
        <View style={styles.servicesGrid}>
          {services.map((service) => {
            const statusColors = getStatusColor(service.status);
            return (
              <View key={service.id} style={styles.serviceCard}>
                <View style={styles.serviceHeader}>
                  <View style={styles.serviceInfo}>
                    <Text style={styles.serviceName}>{getServiceLabel(service.name)}</Text>
                    <Text style={styles.serviceProvider}>{service.provider}</Text>
                  </View>
                  <View style={[styles.statusBadge, { backgroundColor: statusColors.bg }]}>
                    <Text style={[styles.statusText, { color: statusColors.text }]}>
                      {service.status.charAt(0).toUpperCase() + service.status.slice(1)}
                    </Text>
                  </View>
                </View>

                <View style={styles.serviceDetails}>
                  <View style={styles.serviceDetailRow}>
                    <Calendar size={16} color="#4b5563" />
                    <Text style={styles.serviceDetailText}>
                      {getFrequencyLabel(service.frequency)}
                    </Text>
                  </View>
                  <View style={styles.serviceDates}>
                    <View style={styles.serviceDateRow}>
                      <Text style={styles.serviceDateLabel}>Last service:</Text>
                      <Text style={styles.serviceDateValue}>
                        {new Date(service.last_service_date).toLocaleDateString()}
                      </Text>
                    </View>
                    <View style={styles.serviceDateRow}>
                      <Text style={styles.serviceDateLabel}>Next service:</Text>
                      <Text style={styles.serviceDateValue}>
                        {new Date(service.next_service_date).toLocaleDateString()}
                      </Text>
                    </View>
                  </View>
                </View>

                {service.notes && (
                  <View style={styles.notesContainer}>
                    <Text style={styles.notesText}>{service.notes}</Text>
                  </View>
                )}
              </View>
            );
          })}
        </View>
      )}

      <Modal
        visible={isFormOpen}
        transparent
        animationType="slide"
        onRequestClose={() => setIsFormOpen(false)}
      >
        <View style={styles.modalOverlay}>
          <Pressable style={styles.modalBackdrop} onPress={() => setIsFormOpen(false)} />
          <View style={styles.modalContent}>
            <WebScrollView contentContainerStyle={styles.modalScrollContent}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Add Managed Service</Text>
                <Pressable
                  onPress={() => setIsFormOpen(false)}
                  style={styles.modalCloseButton}
                >
                  <X size={20} color="#9ca3af" />
                </Pressable>
              </View>

              <View style={styles.form}>
                <View style={styles.formGroup}>
                  <HomePicker label="Home" compact />
                </View>
                <View style={styles.formGroup}>
                  <Text style={styles.label}>Service Type</Text>
                  <Pressable
                    onPress={() => setShowServiceTypePicker(true)}
                    style={styles.pickerButton}
                  >
                    <Text style={styles.pickerButtonText}>
                      {serviceTypes.find(t => t.id === formData.name)?.label || formData.name}
                    </Text>
                    <ChevronDown size={20} color="#4b5563" />
                  </Pressable>
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.label}>Service Provider</Text>
                  <TextInput
                    style={styles.input}
                    value={formData.provider}
                    onChangeText={(value) => setFormData({ ...formData, provider: value })}
                    placeholder="Enter provider name"
                  />
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.label}>Service Frequency</Text>
                  <Pressable
                    onPress={() => setShowFrequencyPicker(true)}
                    style={styles.pickerButton}
                  >
                    <Text style={styles.pickerButtonText}>
                      {frequencies.find(f => f.value === formData.frequency)?.label || formData.frequency}
                    </Text>
                    <ChevronDown size={20} color="#4b5563" />
                  </Pressable>
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.label}>Last Service Date</Text>
                  <TextInput
                    style={styles.input}
                    value={formData.last_service_date}
                    onChangeText={(value) => setFormData({ ...formData, last_service_date: value })}
                    placeholder="YYYY-MM-DD"
                  />
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.label}>Notes</Text>
                  <TextInput
                    style={[styles.input, styles.textArea]}
                    value={formData.notes}
                    onChangeText={(value) => setFormData({ ...formData, notes: value })}
                    placeholder="Optional notes about the service"
                    multiline
                    numberOfLines={3}
                  />
                </View>

                {error && (
                  <Text style={styles.errorText}>{error}</Text>
                )}

                <View style={styles.formButtons}>
                  <Pressable
                    onPress={() => setIsFormOpen(false)}
                    style={styles.cancelButton}
                  >
                    <Text style={styles.cancelButtonText}>Cancel</Text>
                  </Pressable>
                  <Pressable
                    onPress={handleSubmit}
                    disabled={submitting}
                    style={[styles.submitButton, submitting && styles.submitButtonDisabled]}
                  >
                    {submitting ? (
                      <View style={styles.submitContent}>
                        <ActivityIndicator size="small" color="#ffffff" />
                        <Text style={styles.submitButtonText}>Adding...</Text>
                      </View>
                    ) : (
                      <Text style={styles.submitButtonText}>Add Service</Text>
                    )}
                  </Pressable>
                </View>
              </View>
            </WebScrollView>
          </View>
        </View>
      </Modal>

      {/* Service Type Picker Modal */}
      <Modal
        visible={showServiceTypePicker}
        transparent
        animationType="slide"
        onRequestClose={() => setShowServiceTypePicker(false)}
      >
        <View style={styles.pickerModalOverlay}>
          <Pressable style={styles.pickerModalBackdrop} onPress={() => setShowServiceTypePicker(false)} />
          <View style={styles.pickerModalContent}>
            <View style={styles.pickerModalHeader}>
              <Text style={styles.pickerModalTitle}>Select Service Type</Text>
              <Pressable onPress={() => setShowServiceTypePicker(false)}>
                <X size={20} color="#9ca3af" />
              </Pressable>
            </View>
            <WebScrollView style={styles.pickerModalList}>
              {serviceTypes.map((type) => (
                <Pressable
                  key={type.id}
                  onPress={() => {
                    setFormData({ ...formData, name: type.id });
                    setShowServiceTypePicker(false);
                  }}
                  style={styles.pickerModalItem}
                >
                  <Text style={[
                    styles.pickerModalItemText,
                    formData.name === type.id && styles.pickerModalItemTextSelected
                  ]}>
                    {type.label}
                  </Text>
                </Pressable>
              ))}
            </WebScrollView>
          </View>
        </View>
      </Modal>

      {/* Frequency Picker Modal */}
      <Modal
        visible={showFrequencyPicker}
        transparent
        animationType="slide"
        onRequestClose={() => setShowFrequencyPicker(false)}
      >
        <View style={styles.pickerModalOverlay}>
          <Pressable style={styles.pickerModalBackdrop} onPress={() => setShowFrequencyPicker(false)} />
          <View style={styles.pickerModalContent}>
            <View style={styles.pickerModalHeader}>
              <Text style={styles.pickerModalTitle}>Select Frequency</Text>
              <Pressable onPress={() => setShowFrequencyPicker(false)}>
                <X size={20} color="#9ca3af" />
              </Pressable>
            </View>
            <WebScrollView style={styles.pickerModalList}>
              {frequencies.map((frequency) => (
                <Pressable
                  key={frequency.value}
                  onPress={() => {
                    setFormData({ ...formData, frequency: frequency.value });
                    setShowFrequencyPicker(false);
                  }}
                  style={styles.pickerModalItem}
                >
                  <Text style={[
                    styles.pickerModalItemText,
                    formData.frequency === frequency.value && styles.pickerModalItemTextSelected
                  ]}>
                    {frequency.label}
                  </Text>
                </Pressable>
              ))}
            </WebScrollView>
          </View>
        </View>
      </Modal>
    </WebScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    gap: 32,
    paddingBottom: 16,
  },
  header: {
    gap: 16,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  backButton: {
    padding: 8,
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontSize: 30,
    fontWeight: 'bold',
    color: '#111827',
  },
  subtitle: {
    fontSize: 16,
    color: '#4b5563',
    marginTop: 4,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  addButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '500',
  },
  loadingContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 48,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 48,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 48,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    color: '#4b5563',
    marginBottom: 24,
  },
  emptyAddButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderRadius: 8,
  },
  emptyAddButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '500',
  },
  servicesGrid: {
    ...(Platform.OS === 'web' && {
      flexDirection: 'row',
      flexWrap: 'wrap',
    }),
    gap: 24,
  },
  serviceCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 24,
    gap: 16,
    ...(Platform.OS === 'web' && {
      flex: 1,
      minWidth: 300,
      maxWidth: 400,
    }),
    ...(Platform.OS === 'web' && {
      ...shadows.sm,
    }),
  },
  serviceHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  serviceInfo: {
    flex: 1,
  },
  serviceName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  serviceProvider: {
    fontSize: 14,
    color: '#4b5563',
    marginTop: 4,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '500',
  },
  serviceDetails: {
    gap: 8,
  },
  serviceDetailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  serviceDetailText: {
    fontSize: 14,
    color: '#4b5563',
  },
  serviceDates: {
    gap: 4,
  },
  serviceDateRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  serviceDateLabel: {
    fontSize: 14,
    color: '#4b5563',
  },
  serviceDateValue: {
    fontSize: 14,
    color: '#4b5563',
  },
  notesContainer: {
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
  },
  notesText: {
    fontSize: 14,
    color: '#4b5563',
  },
  modalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    padding: 16,
  },
  modalBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    width: '100%',
    maxWidth: 448,
    maxHeight: '80%',
    ...(Platform.OS === 'web' && {
      ...shadows.lg,
    }),
  },
  modalScrollContent: {
    padding: 24,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  modalCloseButton: {
    padding: 4,
  },
  form: {
    gap: 16,
  },
  formGroup: {
    gap: 4,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 4,
  },
  pickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    backgroundColor: '#ffffff',
  },
  pickerButtonText: {
    fontSize: 16,
    color: '#111827',
    flex: 1,
  },
  pickerModalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  pickerModalBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  pickerModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '50%',
    ...(Platform.OS === 'web' && {
      ...shadows.bottom,
    }),
  },
  pickerModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  pickerModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  pickerModalList: {
    maxHeight: 300,
  },
  pickerModalItem: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  pickerModalItemText: {
    fontSize: 16,
    color: '#111827',
  },
  pickerModalItemTextSelected: {
    fontWeight: '600',
    color: '#000000',
  },
  input: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 16,
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none' as any,
    }),
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  errorText: {
    color: '#ef4444',
    fontSize: 14,
  },
  formButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 8,
  },
  cancelButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  cancelButtonText: {
    fontSize: 14,
    color: '#4b5563',
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderRadius: 8,
  },
  submitButtonDisabled: {
    opacity: 0.5,
  },
  submitContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  submitButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
});

export default ManagedServices;
